import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Calendar, AlertTriangle, Package, Truck, Bell } from 'lucide-react';
import KPICard from '../../components/KPICard';
import DataTable from '../../components/DataTable';
import { reportsApi } from '../../services/api';
import { formatNumber } from '../../utils/formatters';
import { funnelSql, STAGE_LABEL, clientLabel } from './cohortFunnel';
import GuidedTour, { GuideButton } from './GuidedTour';

// Guided-tour steps — anchored to the data-tour="…" attributes below. Copy only, PII-free.
const OH_TOUR = [
  { target: 'oh-controls', tag: 'Ops Health', title: 'Window & tolerance', body: 'Set the date range and the tolerance %. A client is flagged only when cards not made exceed this share of what was requested — filtering out routine noise.' },
  { target: 'oh-kpis', tag: 'Ops Health', title: 'Funnel totals', body: 'Totals across all clients for each stage — Sunny requested through Shipped — plus in-transit and how many clients are flagged.' },
  { target: 'oh-exceptions', tag: 'Ops Health', title: 'Exceptions', body: 'Clients where cards were requested but never made, beyond tolerance — what leadership acts on. An empty list means everything reconciled.' },
  { target: 'oh-table', tag: 'Ops Health', title: 'Lifecycle by client', body: '“Not made” = requested minus cards FIS made. AbCorp produced tracks Shipped in count because AB Corp reports only at ship.' },
];
const OH_TOUR_KEY = 'printops.oh.tour.v1';

// Print Ops Health — the leadership reconciliation view over the SAME cohort card-order funnel
// as the File Tracker (shared cohortFunnel.js), so the two pages can never drift. Stages:
//   Sunny requested -> FIS embossed -> AbCorp produced -> Shipped to member
// Each stage is a strict subset of the prior (monotone), so shipped lagging production is
// EXPECTED (in-flight), NOT a deviation. The real reconciliation signal is "Not embossed" =
// cards Sunny requested that FIS never minted a card for (sent-but-no-card, S1). If the live
// query is unavailable the table is empty — never faked. PII-free: aggregate COUNT / proxy only.
//
// The "Sent to AB Corp" handoff (NAF file received by AB Corp, before production) has no
// per-card signal in the warehouse yet — shown as instrumentation-pending between FIS embossed
// and AbCorp produced, rather than hidden.

const iso = (d) => d.toISOString().slice(0, 10);
const defaultRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 21);
  return { start: iso(start), end: iso(end) };
};

// Window dates are date-only (YYYY-MM-DD, from <input type=date>). Format in UTC so a date such as
// 2026-10-06 isn't shown as the prior day: the shared formatDate reads it at UTC midnight and a
// behind-UTC timezone (e.g. America/New_York) then renders it as Oct 5.
const fmtDay = (s) => {
  if (!s) return '';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
};

export default function PrintOpsHealth() {
  const [dateRange, setDateRange] = useState(defaultRange);
  const [tolerancePct, setTolerancePct] = useState(1);
  const [tourOpen, setTourOpen] = useState(false);

  // First-visit auto-start (remembered); the Guide button reopens it anytime.
  useEffect(() => {
    try { if (!localStorage.getItem(OH_TOUR_KEY)) setTourOpen(true); } catch (e) { /* ignore */ }
  }, []);

  // An inverted window (start after end) would run a BETWEEN that returns no rows and read as a
  // clean "No exceptions". min/max on the inputs only mark typed values invalid — they don't stop
  // onChange — so guard it here: skip the query and show an explicit notice instead of an all-clear.
  const invalidRange = !!dateRange.start && !!dateRange.end && dateRange.start > dateRange.end;

  // Same cohort funnel the File Tracker uses (shared module). If the POST fails, rows are
  // empty — never faked — and isError drives a visible failure banner so a failed query is
  // never mistaken for a clean reconciliation (see the Exceptions section below).
  const { data: funnelData, isLoading, isError, isFetching, isPlaceholderData } = useQuery({
    queryKey: ['opsHealthFunnel', dateRange.start, dateRange.end],
    queryFn: () => reportsApi.executeCustomQuery(funnelSql(dateRange.start, dateRange.end)),
    enabled: !!dateRange.start && !!dateRange.end && !invalidRange,
    retry: false,
    placeholderData: keepPreviousData,   // keep the prior window's rows while a new window loads
  });
  // Previous window's rows are still on screen while a newly-changed range loads — they are NOT
  // the current window's figures, so callers surface an "updating" hint rather than present them
  // as reconciled for the new dates.
  const stale = isFetching && isPlaceholderData;

  const { rows, totals, alerts } = useMemo(() => {
    const out = (funnelData?.data || []).map((r) => {
      const requested = Number(r.requested || 0);   // Sunny requested (FIS_BATCH_SENT)
      const created = Number(r.created || 0);        // Proxy created (FIS proxy)
      const embossed = Number(r.embossed || 0);      // FIS embossed ('Embossing Complete')
      const produced = Number(r.produced || 0);      // AbCorp produced (processing_date)
      const shipped = Number(r.shipped || 0);        // Shipped to member (ship_date)
      const notMade = Math.max(requested - created, 0);   // sent-but-no-card (S1 signal)
      const inTransit = Math.max(embossed - shipped, 0);  // embossed, not yet shipped
      // Deviation = cards sent to FIS that never got a proxy, beyond the tolerance share of what
      // was requested. Compared as an exact percentage (NOT rounded up) — rounding the allowance
      // up to a whole card let a small client with a 100% gap (1 requested, 0 made) read as OK.
      const tol = (tolerancePct / 100) * requested;
      const deviation = requested > 0 && notMade > tol;
      return {
        client: clientLabel(r.tenant_name),
        requested, created, embossed, produced, shipped, notMade, inTransit, tol, deviation,
        member: Number(r.requested_member || 0),
        admin: Number(r.requested_admin || 0),
        bulk: Number(r.requested_bulk || 0),
        status: deviation ? 'Review' : 'OK',
      };
    });
    out.sort((a, b) => (b.notMade - a.notMade) || (b.requested - a.requested));
    const totals = out.reduce(
      (acc, r) => ({
        requested: acc.requested + r.requested,
        created: acc.created + r.created,
        embossed: acc.embossed + r.embossed,
        produced: acc.produced + r.produced,
        shipped: acc.shipped + r.shipped,
        notMade: acc.notMade + r.notMade,
        inTransit: acc.inTransit + r.inTransit,
        deviations: acc.deviations + (r.deviation ? 1 : 0),
      }),
      { requested: 0, created: 0, embossed: 0, produced: 0, shipped: 0, notMade: 0, inTransit: 0, deviations: 0 }
    );
    const alerts = out.filter((r) => r.deviation);
    return { rows: out, totals, alerts };
  }, [funnelData, tolerancePct]);

  const columns = [
    { key: 'client', label: 'Client', sortable: true },
    { key: 'requested', label: STAGE_LABEL.Requested, sortable: true, format: 'number' },
    { key: 'created', label: STAGE_LABEL.Created, sortable: true, format: 'number' },
    { key: 'embossed', label: STAGE_LABEL.Embossed, sortable: true, format: 'number' },
    { key: 'produced', label: STAGE_LABEL.Produced, sortable: true, format: 'number' },
    { key: 'shipped', label: STAGE_LABEL.Shipped, sortable: true, format: 'number' },
    { key: 'notMade', label: 'Not made', sortable: true, format: 'number' },
    { key: 'status', label: 'Status', sortable: true },
  ];

  const kpis = [
    { title: STAGE_LABEL.Requested, value: totals.requested, icon: Package, format: 'compact' },
    { title: STAGE_LABEL.Created, value: totals.created, icon: Package, format: 'compact' },
    { title: STAGE_LABEL.Embossed, value: totals.embossed, icon: Package, format: 'compact' },
    { title: STAGE_LABEL.Produced, value: totals.produced, icon: Truck, format: 'compact' },
    { title: STAGE_LABEL.Shipped, value: totals.shipped, icon: Truck, format: 'compact' },
    { title: 'In transit', value: totals.inTransit, icon: AlertTriangle, format: 'compact' },
    { title: 'Clients flagged', value: totals.deviations, icon: Bell, format: 'number' },
  ];

  // The Exceptions panel must never show an all-clear for a state it hasn't actually checked:
  // an inverted window, a failed query, or a still-loading first fetch each gets its own notice;
  // only a completed, successful, empty result reads as "No exceptions".
  const renderExceptions = () => {
    if (invalidRange) return <p className="text-sm text-amber-700">Start date is after the end date — adjust the window to run reconciliation.</p>;
    if (isError) return <p className="text-sm text-red-700">Can’t confirm exceptions — the reconciliation query failed, so this is not an all-clear. Retry shortly.</p>;
    if (isLoading || stale) return <p className="text-sm text-gray-500">Checking for exceptions…</p>;
    if (alerts.length === 0) return <p className="text-sm text-gray-500">No exceptions — every requested card was made by FIS within tolerance ({tolerancePct}%).</p>;
    return (
      <div className="space-y-2">
        {alerts.map((a) => (
          <div key={a.client} className="flex items-start justify-between gap-4 border-l-2 border-red-400 bg-red-50/60 rounded-r px-3 py-2">
            <div>
              <p className="font-medium text-gray-900">{a.client}</p>
              <p className="text-sm text-gray-500 mt-0.5">
                {formatNumber(a.requested)} requested, {formatNumber(a.created)} made
                {' · '}{fmtDay(dateRange.start)}–{fmtDay(dateRange.end)} · tolerance {tolerancePct}%
              </p>
            </div>
            <span className="text-red-700 font-semibold whitespace-nowrap">{formatNumber(a.notMade)} not made</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Print Ops Health</h2>
          <p className="text-gray-500 mt-1 text-sm sm:text-base">
            Card-order lifecycle reconciliation by client
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div data-tour="oh-controls" className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-gray-400" />
              {/* max/min keep start <= end so an inverted window can't silently return zero rows
                  and read as a clean "No exceptions" all-clear. */}
              <input type="date" value={dateRange.start} max={dateRange.end}
                onChange={(e) => e.target.value && setDateRange({ ...dateRange, start: e.target.value })} className="input w-40" />
              <span className="text-gray-500">to</span>
              <input type="date" value={dateRange.end} min={dateRange.start}
                onChange={(e) => e.target.value && setDateRange({ ...dateRange, end: e.target.value })} className="input w-40" />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Tolerance %</span>
              <input type="number" min="0" step="0.5" value={tolerancePct}
                onChange={(e) => setTolerancePct(Number(e.target.value))} className="input w-20" />
            </div>
          </div>
          <GuideButton onClick={() => setTourOpen(true)} />
        </div>
      </div>

      {invalidRange && (
        <div className="border-l-2 border-amber-400 bg-amber-50/70 rounded-r px-4 py-3 text-sm text-amber-800">
          The start date is after the end date — reconciliation is paused. The figures below are from the last valid
          window, <strong>not this range</strong>. Adjust the dates to run it again.
        </div>
      )}
      {isError && !invalidRange && (
        <div className="border-l-2 border-red-400 bg-red-50/60 rounded-r px-4 py-3 text-sm text-red-700">
          Couldn’t load card-order data — the warehouse query failed. The figures below are incomplete and this is{' '}
          <strong>not an all-clear</strong>. Retry shortly; if it persists, check the Redshift connection.
        </div>
      )}
      {stale && !invalidRange && (
        <p className="text-xs text-amber-600">Updating — showing the previous window while the new range loads…</p>
      )}

      <div data-tour="oh-kpis" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {kpis.map((kpi, i) => (<KPICard key={i} {...kpi} loading={isLoading} />))}
      </div>

      <div className="card" data-tour="oh-exceptions">
        <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">Exceptions</h3>
        {renderExceptions()}
      </div>

      <div className="card" data-tour="oh-table">
        <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-6">Lifecycle by Client</h3>
        <DataTable data={rows} columns={columns} loading={isLoading}
          emptyMessage="No card orders for the selected period" />
        <p className="text-xs text-gray-400 mt-4">
          Not made = requested minus cards FIS made — the reconciliation signal. AbCorp produced tracks Shipped in
          count because AB Corp reports only at ship. Figures are aggregate, by client.
        </p>
      </div>

      <GuidedTour steps={OH_TOUR} open={tourOpen} onClose={() => setTourOpen(false)} storageKey={OH_TOUR_KEY} />
    </div>
  );
}
